/**
 * The client portal domain API's vocabulary, reduced to the decisions the
 * settings card has to make.
 *
 * Deliberately pure — no React, no translations, no `@repo/core` runtime
 * import (the DTO types are type-only and erased at compile time). The
 * functions return *tokens*; `PortalDomainForm` turns a token into a
 * translated string. That keeps the branching testable without a DOM and stops
 * an unmapped server code from reaching the user as a raw enum.
 */

import type { PortalDnsRecord, PortalDomainDto } from "@repo/core/client";

/** Leaf keys under `organizations.settings.clientPortal.errors`. */
export type PortalDomainErrorKey =
	| "required"
	| "invalidHostname"
	| "canonicalHostname"
	| "reservedHostname"
	| "alreadyAttached"
	| "hostnameTaken"
	| "provider"
	| "generic";

/**
 * Server `error` codes → copy. Anything absent here is a code the UI has never
 * been taught, which must read as a generic failure rather than as
 * "DOMAIN_PROVIDER_ERROR" in a toast.
 */
const CODE_TO_ERROR_KEY: Record<string, PortalDomainErrorKey> = {
	INVALID_HOSTNAME: "invalidHostname",
	CANONICAL_HOSTNAME: "canonicalHostname",
	RESERVED_HOSTNAME: "reservedHostname",
	ALREADY_ATTACHED: "alreadyAttached",
	HOSTNAME_TAKEN: "hostnameTaken",
	DOMAIN_PROVIDER_ERROR: "provider",
};

/**
 * A non-2xx from the portal-domain routes, carrying the machine-readable code
 * alongside the server's own prose. The prose is a fallback: we prefer our
 * translated copy, because the server's is English-only.
 */
export class PortalDomainRequestError extends Error {
	readonly code: string;
	readonly status: number;

	constructor(code: string, message: string, status: number) {
		super(message);
		this.name = "PortalDomainRequestError";
		this.code = code;
		this.status = status;
	}
}

/** Which translated error line to show for a failed mutation. */
export function portalDomainErrorKey(error: unknown): PortalDomainErrorKey {
	if (error instanceof PortalDomainRequestError) {
		return CODE_TO_ERROR_KEY[error.code] ?? "generic";
	}

	return "generic";
}

/**
 * The browser-side hostname validator's rejection reason → the same copy the
 * server would have produced, so an inline field error and a server error read
 * identically.
 */
export function portalHostnameReasonKey(
	reason: "invalid" | "canonical" | "reserved_suffix",
): PortalDomainErrorKey {
	switch (reason) {
		case "canonical":
			return "canonicalHostname";
		case "reserved_suffix":
			return "reservedHostname";
		default:
			return "invalidHostname";
	}
}

/** Badge `status` for a domain's verification state. */
export function portalDomainBadgeStatus(
	status: PortalDomainDto["status"],
): "success" | "warning" | "error" {
	switch (status) {
		case "ACTIVE":
			return "success";
		case "ERROR":
			return "error";
		default:
			return "warning";
	}
}

/**
 * The DNS rows to render.
 *
 * The service always writes a CNAME row alongside any TXT records the provider
 * asked for — but only once a status poll has succeeded. A domain attached
 * while the provider was down is persisted with no records at all, and that is
 * exactly the state in which the agency most needs to see the CNAME. So the
 * CNAME is synthesised from the hostname and the target when the stored list
 * is empty.
 */
export function portalDnsRows(
	domain: Pick<PortalDomainDto, "hostname" | "verificationRecords">,
	cnameTarget: string,
): PortalDnsRecord[] {
	if (domain.verificationRecords.length > 0) {
		return domain.verificationRecords;
	}

	// The API normally fills this in with the public-suffix list; this only
	// runs when the attach call failed before any record was stored.
	const labels = domain.hostname.split(".").filter(Boolean);
	const zone =
		labels.length >= 3
			? labels.slice(-2).join(".")
			: labels.slice(-1).join(".");
	const name =
		labels.length >= 3 ? labels.slice(0, -2).join(".") : labels[0] || "@";
	return [{ type: "CNAME", name, value: cnameTarget, zone }];
}
